import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  MessageCircle, Pencil, Check, Scissors, Star, BookOpen,
  Award, Zap, ChevronDown, X, Menu, Plus, Trash2, Users, Loader2
} from 'lucide-react'
import { SiInstagram, SiFacebook } from 'react-icons/si'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EditableText } from '@/components/EditableText'
import { EditableImage } from '@/components/EditableImage'
import { DEFAULT_IMAGES, DEFAULT_MODULES, MODULES_CONTENT_KEY, type FormationModule } from '@/lib/index'
import { loadAllContent, saveContent, getStorageUrl, imageSlotToFileName, recordVisit } from '@/integrations/supabase/client'
import { staggerContainer, staggerItem } from '@/lib/motion'

const WA = 'https://wa.me/33681199619?text=Je%20d%C3%A9sire%20des%20informations%20sur%20tes%20formations'

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  scissors: Scissors, star: Star, users: Users, book: BookOpen, award: Award, zap: Zap,
}

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function ImgWithFallback({ src, fallback, alt, className }: { src: string; fallback: string; alt: string; className?: string }) {
  return <img src={src} alt={alt} className={className} onError={e => { (e.target as HTMLImageElement).src = fallback }} />
}

// ─── Module Modal ─────────────────────────────────────────────────────────────
function ModuleModal({ open, onClose, mod, onSave, onDelete }: {
  open: boolean; onClose: () => void; mod: FormationModule | null
  onSave: (m: FormationModule) => void; onDelete?: () => void
}) {
  const blank: FormationModule = { id: '', title: '', description: '', duration: '', level: '', icon: 'scissors' }
  const [form, setForm] = useState<FormationModule>(blank)

  useEffect(() => {
    setForm(mod ? { ...mod } : { ...blank, id: Date.now().toString() })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod, open])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{mod ? 'Modifier le module' : 'Nouveau module'}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-muted-foreground" /></button>
        </div>
        <div className="space-y-3">
          <div><Label>Titre</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
          <div><Label>Description</Label><Textarea value={form.description} rows={3} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Durée</Label><Input value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} /></div>
            <div><Label>Niveau</Label><Input value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))} /></div>
          </div>
          <div>
            <Label>Icône</Label>
            <Select value={form.icon} onValueChange={v => setForm(f => ({ ...f, icon: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(ICON_MAP).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => onSave(form)}>Enregistrer</Button>
          {onDelete && <Button variant="destructive" size="icon" onClick={onDelete}><Trash2 className="w-4 h-4" /></Button>}
        </div>
      </div>
    </div>
  )
}

// ─── Home ─────────────────────────────────────────────────────────────────────
export default function Home() {
  const [loading, setLoading] = useState(true)
  const [remoteContent, setRemoteContent] = useState<Record<string, string>>({})
  const [images, setImages] = useState<Record<string, string>>(DEFAULT_IMAGES)
  const [modules, setModules] = useState<FormationModule[]>(DEFAULT_MODULES)
  const [editMode, setEditMode] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingMod, setEditingMod] = useState<FormationModule | null>(null)
  const [lightbox, setLightbox] = useState<string | null>(null)

  // ── Charger TOUT le contenu depuis Supabase au démarrage ──────────────────
  const loadContent = useCallback(async () => {
    setLoading(true)
    try {
      const data = await loadAllContent()
      setRemoteContent(data)

      // Reconstruire les URLs d'images depuis Supabase Storage (avec cache-buster)
      const newImages = { ...DEFAULT_IMAGES }
      const allSlots = [
        'hero.bg', 'about.photo',
        'gallery.1', 'gallery.2', 'gallery.3', 'gallery.4', 'gallery.5', 'gallery.6',
      ]

      // Charger les modules sauvegardés
      if (data[MODULES_CONTENT_KEY]) {
        try {
          const savedMods = JSON.parse(data[MODULES_CONTENT_KEY]) as FormationModule[]
          setModules(savedMods)
          savedMods.forEach(m => allSlots.push(`formation.${m.id}`))
        } catch { /* garde les modules par défaut */ }
      } else {
        DEFAULT_MODULES.forEach(m => allSlots.push(`formation.${m.id}`))
      }

      // Pour chaque slot, si une URL est sauvegardée dans site_content, l'utiliser
      for (const slot of allSlots) {
        const imgKey = `__img__${slot}`
        if (data[imgKey]) {
          // URL complète sauvegardée directement
          newImages[slot] = data[imgKey]
        } else {
          // Sinon essayer de récupérer depuis Supabase Storage
          const fileName = imageSlotToFileName(slot)
          const storageUrl = getStorageUrl(fileName)
          // Vérifier si le fichier existe en ajoutant un timestamp pour éviter le cache 404
          newImages[slot] = storageUrl
        }
      }
      setImages(newImages)
    } catch (err) {
      console.warn('Erreur chargement contenu:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadContent() }, [loadContent])
  useEffect(() => { recordVisit() }, [])

  // ── Quand une image est uploadée → sauvegarder l'URL dans Supabase ────────
  function handleNewImage(slotId: string, publicUrl: string) {
    // Sauvegarder l'URL dans la table site_content avec préfixe __img__
    saveContent(`__img__${slotId}`, publicUrl)
    setImages(prev => ({ ...prev, [slotId]: publicUrl }))
  }

  function getImg(slotId: string): string {
    return images[slotId] || DEFAULT_IMAGES[slotId] || ''
  }

  function openModal(mod: FormationModule | null) { setEditingMod(mod); setModalOpen(true) }

  function handleSaveMod(m: FormationModule) {
    const updated = modules.find(x => x.id === m.id)
      ? modules.map(x => x.id === m.id ? m : x)
      : [...modules, m]
    setModules(updated)
    // Sauvegarder les modules sur Supabase
    saveContent(MODULES_CONTENT_KEY, JSON.stringify(updated))
    setModalOpen(false)
  }

  function handleDeleteMod() {
    if (!editingMod) return
    const updated = modules.filter(x => x.id !== editingMod.id)
    setModules(updated)
    saveContent(MODULES_CONTENT_KEY, JSON.stringify(updated))
    setModalOpen(false)
  }

  // ── Affichage chargement ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-lg border-2 border-primary flex items-center justify-center mx-auto mb-4">
            <span className="text-primary font-black">SB</span>
          </div>
          <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* Bouton édition — totalement invisible pour les visiteurs */}
      <div className="fixed top-20 right-4 z-50">
        <Button size="sm" onClick={() => setEditMode(v => !v)}
          className={editMode
            ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg'
            : 'bg-transparent text-transparent border-0 opacity-0 hover:opacity-20 hover:bg-muted hover:text-muted-foreground transition-all'}
          title="Mode édition">
          {editMode ? <><Check className="w-4 h-4 mr-1" />Terminer</> : <Pencil className="w-4 h-4" />}
        </Button>
      </div>

      {/* ── HEADER ── */}
      <header className="fixed top-0 left-0 right-0 h-16 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg border-2 border-primary flex items-center justify-center">
              <span className="text-primary font-black text-sm">SB</span>
            </div>
            <span className="font-semibold hidden sm:block">Sidi Bensoula</span>
          </button>
          <div className="flex items-center gap-2">
            <a href={WA} target="_blank" rel="noopener noreferrer" className="hidden md:block">
              <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1">
                <MessageCircle className="w-4 h-4" />Me contacter
              </Button>
            </a>
            <button className="md:hidden text-muted-foreground" onClick={() => setMenuOpen(v => !v)}>
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile nav */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="fixed top-16 left-0 right-0 z-30 bg-background border-b border-border p-4">
            <a href={WA} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)}>
              <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                <MessageCircle className="w-4 h-4" />Me contacter sur WhatsApp
              </Button>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HERO ── */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {editMode ? (
            <EditableImage slotId="hero.bg" src={getImg('hero.bg')} alt="Hero background"
              className="w-full h-full" imgClassName="opacity-30" editMode={editMode} onNewImage={handleNewImage} />
          ) : (
            <ImgWithFallback src={getImg('hero.bg')} fallback={DEFAULT_IMAGES['hero.bg']}
              alt="Hero background" className="w-full h-full object-cover opacity-30" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/80" />
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-4xl mx-auto">
            <motion.div variants={staggerItem} className="mb-6">
              <span className="px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                <EditableText id="hero.badge" editMode={editMode} remoteContent={remoteContent} />
              </span>
            </motion.div>
            <motion.h1 variants={staggerItem} className="text-5xl md:text-7xl font-bold mb-4 font-heading">
              <EditableText id="hero.name" editMode={editMode} remoteContent={remoteContent} />
            </motion.h1>
            <motion.p variants={staggerItem} className="text-2xl md:text-3xl text-primary mb-4">
              <EditableText id="hero.tagline" editMode={editMode} remoteContent={remoteContent} />
            </motion.p>
            <motion.p variants={staggerItem} className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              <EditableText id="hero.subtitle" editMode={editMode} remoteContent={remoteContent} multiline />
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-wrap gap-4 justify-center mb-12">
              <button onClick={() => scrollTo('formations')}>
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <EditableText id="hero.cta1" editMode={editMode} remoteContent={remoteContent} />
                </Button>
              </button>
              <a href={WA} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                  <MessageCircle className="w-4 h-4" /><EditableText id="hero.cta2" editMode={editMode} remoteContent={remoteContent} />
                </Button>
              </a>
            </motion.div>
            <motion.div variants={staggerItem} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-card/50 backdrop-blur border border-border rounded-xl p-4 text-left">
                  <p className="font-semibold mb-1"><EditableText id={`hero.pill${i}.title`} editMode={editMode} remoteContent={remoteContent} /></p>
                  <p className="text-xs text-muted-foreground"><EditableText id={`hero.pill${i}.desc`} editMode={editMode} remoteContent={remoteContent} multiline /></p>
                </div>
              ))}
            </motion.div>
          </motion.div>
          <button onClick={() => scrollTo('about')} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground animate-bounce">
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* ── FINANCEMENT ── */}
      <section id="financement" className="py-16 bg-primary/5 border-y border-primary/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.div variants={staggerItem} className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <span className="text-primary text-xl">💰</span>
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold font-heading text-primary mb-4 border-l-4 border-primary pl-4">Financement de vos formations</h2>
                <div className="space-y-3 text-muted-foreground leading-relaxed">
                  <p>Mes formations peuvent être éligibles à une prise en charge par votre <span className="text-foreground font-medium">OPCO</span> ou par le <span className="text-foreground font-medium">FAFCEA</span>, selon votre statut professionnel et les critères de financement en vigueur.</p>
                  <p>Les dossiers de demande de prise en charge sont réalisés en collaboration avec le cabinet <span className="text-foreground font-medium">2C2C Formations</span>, organisme certifié <span className="text-primary font-medium">Qualiopi</span>, afin de vous accompagner dans vos démarches administratives.</p>
                  <p>Que vous soyez <span className="text-foreground font-medium">salarié, chef d'entreprise, artisan ou indépendant</span>, un accompagnement peut être proposé pour faciliter votre demande de financement.</p>
                  <p className="text-sm italic border-l-2 border-primary/40 pl-3">Les prises en charge restent soumises à validation par l'organisme financeur.</p>
                </div>
                <div className="mt-5">
                  <a href={WA} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                      <MessageCircle className="w-4 h-4" />Me renseigner sur le financement
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-24 bg-card/30">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div variants={staggerItem}>
              {editMode ? (
                <EditableImage slotId="about.photo" src={getImg('about.photo')} alt="Sidi Bensoula"
                  className="rounded-2xl overflow-hidden w-full max-w-md mx-auto"
                  editMode={editMode} onNewImage={handleNewImage} forcePortrait />
              ) : (
                <div className="aspect-[3/4] w-full max-w-md mx-auto rounded-2xl overflow-hidden">
                  <ImgWithFallback src={getImg('about.photo')} fallback={DEFAULT_IMAGES['about.photo']}
                    alt="Sidi Bensoula" className="w-full h-full object-cover" />
                </div>
              )}
            </motion.div>
            <motion.div variants={staggerItem} className="flex flex-col justify-start">
              <span className="text-primary text-sm font-medium uppercase tracking-widest mb-2">
                <EditableText id="about.tag" editMode={editMode} remoteContent={remoteContent} />
              </span>
              <h2 className="text-4xl font-bold mb-3 font-heading border-l-4 border-primary pl-4">
                <EditableText id="about.title" editMode={editMode} remoteContent={remoteContent} />
              </h2>
              <p className="text-lg text-muted-foreground mb-4 italic">
                <EditableText id="about.subtitle" editMode={editMode} remoteContent={remoteContent} multiline />
              </p>
              <EditableText id="about.bio" editMode={editMode} remoteContent={remoteContent} tag="p"
                className="text-muted-foreground leading-relaxed whitespace-pre-line mb-6" multiline />
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="text-center p-3 bg-card rounded-xl border border-border">
                    <p className="text-2xl font-bold text-primary"><EditableText id={`about.stat${i}.number`} editMode={editMode} remoteContent={remoteContent} /></p>
                    <p className="text-xs text-muted-foreground mt-1"><EditableText id={`about.stat${i}.label`} editMode={editMode} remoteContent={remoteContent} multiline /></p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <span key={i} className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm">
                    <EditableText id={`about.val${i}`} editMode={editMode} remoteContent={remoteContent} />
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FORMATIONS ── */}
      <section id="formations" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.div variants={staggerItem} className="text-center mb-12">
              <span className="text-primary text-sm font-medium uppercase tracking-widest block mb-2">
                <EditableText id="formation.tag" editMode={editMode} remoteContent={remoteContent} />
              </span>
              <h2 className="text-4xl font-bold font-heading border-l-4 border-primary pl-4 inline-block text-left">
                <EditableText id="formation.title" editMode={editMode} remoteContent={remoteContent} />
              </h2>
              <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
                <EditableText id="formation.subtitle" editMode={editMode} remoteContent={remoteContent} multiline />
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {modules.map(mod => {
                const Icon = ICON_MAP[mod.icon] ?? Scissors
                const slotId = `formation.${mod.id}`
                return (
                  <motion.div key={mod.id} variants={staggerItem}>
                    <Card className="overflow-hidden h-full flex flex-col hover:border-primary/50 transition-colors">
                      {editMode ? (
                        <EditableImage slotId={slotId} src={getImg(slotId)} alt={mod.title}
                          editMode={editMode} onNewImage={handleNewImage} forcePortrait />
                      ) : (
                        <div className="aspect-[3/4] overflow-hidden">
                          <ImgWithFallback src={getImg(slotId)} fallback={DEFAULT_IMAGES['formation.mod1']}
                            alt={mod.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-5 h-5 text-primary flex-shrink-0" />
                          <h3 className="text-lg font-semibold">{mod.title}</h3>
                        </div>
                        <p className="text-muted-foreground text-sm mb-4 flex-1">{mod.description}</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3">
                          <span>{mod.duration}</span><span>{mod.level}</span>
                        </div>
                        {editMode && (
                          <Button size="sm" variant="outline" className="mt-3" onClick={() => openModal(mod)}>
                            Modifier ce module
                          </Button>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                )
              })}
              {editMode && (
                <motion.div variants={staggerItem}>
                  <button onClick={() => openModal(null)}
                    className="w-full h-full min-h-60 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                    <Plus className="w-8 h-8" /><span className="text-sm">Ajouter un module</span>
                  </button>
                </motion.div>
              )}
            </div>
            <motion.div variants={staggerItem} className="text-center">
              <a href={WA} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                  <MessageCircle className="w-5 h-5" /><EditableText id="formation.cta" editMode={editMode} remoteContent={remoteContent} />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── COLLABORATIONS ── */}
      <section id="collabs" className="py-24 bg-card/30">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.div variants={staggerItem} className="text-center mb-12">
              <span className="text-primary text-sm font-medium uppercase tracking-widest block mb-2">
                <EditableText id="collabs.tag" editMode={editMode} remoteContent={remoteContent} />
              </span>
              <h2 className="text-4xl font-bold font-heading border-l-4 border-primary pl-4 inline-block text-left">
                <EditableText id="collabs.title" editMode={editMode} remoteContent={remoteContent} />
              </h2>
              <p className="text-muted-foreground mt-3"><EditableText id="collabs.subtitle" editMode={editMode} remoteContent={remoteContent} multiline /></p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['wahl', 'ac', 'events'].map(key => (
                <motion.div key={key} variants={staggerItem}>
                  <Card className="p-6 h-full hover:border-primary/50 transition-colors">
                    <h3 className="text-xl font-bold mb-1 text-primary"><EditableText id={`collabs.${key}.name`} editMode={editMode} remoteContent={remoteContent} /></h3>
                    <p className="text-sm text-primary/70 font-medium mb-3"><EditableText id={`collabs.${key}.role`} editMode={editMode} remoteContent={remoteContent} /></p>
                    <p className="text-muted-foreground text-sm"><EditableText id={`collabs.${key}.desc`} editMode={editMode} remoteContent={remoteContent} multiline /></p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section id="galerie" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.div variants={staggerItem} className="text-center mb-12">
              <span className="text-primary text-sm font-medium uppercase tracking-widest block mb-2">
                <EditableText id="gallery.tag" editMode={editMode} remoteContent={remoteContent} />
              </span>
              <h2 className="text-4xl font-bold font-heading border-l-4 border-primary pl-4 inline-block text-left">
                <EditableText id="gallery.title" editMode={editMode} remoteContent={remoteContent} />
              </h2>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(n => {
                const slotId = `gallery.${n}`
                const src = getImg(slotId)
                return (
                  <motion.div key={slotId} variants={staggerItem}>
                    {editMode ? (
                      <EditableImage slotId={slotId} src={src} alt={`Galerie ${n}`}
                        className="rounded-xl overflow-hidden" editMode={editMode}
                        onNewImage={handleNewImage} forcePortrait onClick={() => setLightbox(src)} />
                    ) : (
                      <div className="aspect-[3/4] rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setLightbox(src)}>
                        <ImgWithFallback src={src} fallback={DEFAULT_IMAGES['gallery.1']}
                          alt={`Galerie ${n}`} className="w-full h-full object-cover" />
                      </div>
                    )}
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}>
            <button className="absolute top-4 right-4 text-white" onClick={() => setLightbox(null)}><X className="w-8 h-8" /></button>
            <img src={lightbox} alt="Lightbox" className="max-h-[90vh] max-w-full object-contain rounded-xl" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CONTACT ── */}
      <section id="contact" className="py-24 bg-card/30">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="max-w-2xl mx-auto text-center">
            <motion.div variants={staggerItem}>
              <span className="text-primary text-sm font-medium uppercase tracking-widest block mb-2">
                <EditableText id="contact.tag" editMode={editMode} remoteContent={remoteContent} />
              </span>
              <h2 className="text-4xl font-bold font-heading border-l-4 border-primary pl-4 inline-block text-left mb-4">
                <EditableText id="contact.title" editMode={editMode} remoteContent={remoteContent} />
              </h2>
              <p className="text-muted-foreground mb-8"><EditableText id="contact.subtitle" editMode={editMode} remoteContent={remoteContent} multiline /></p>
            </motion.div>
            <motion.div variants={staggerItem}>
              <Card className="p-6 mb-6 text-left">
                <p className="text-muted-foreground mb-4"><EditableText id="contact.desc" editMode={editMode} remoteContent={remoteContent} multiline /></p>
                <div className="flex flex-col gap-3">
                  <a href="https://www.instagram.com/sidi_bensoula" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors">
                    <SiInstagram className="w-5 h-5 flex-shrink-0" />
                    <EditableText id="contact.instagram" editMode={editMode} remoteContent={remoteContent} />
                  </a>
                  <a href="https://www.facebook.com/sidibensoula" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors">
                    <SiFacebook className="w-5 h-5 flex-shrink-0" />
                    <EditableText id="contact.facebook" editMode={editMode} remoteContent={remoteContent} />
                  </a>
                  <a href="mailto:sidi.bensoula@gmail.com"
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors">
                    <MessageCircle className="w-5 h-5 flex-shrink-0" />
                    <EditableText id="contact.email" editMode={editMode} remoteContent={remoteContent} />
                  </a>
                </div>
              </Card>
              <a href={WA} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                  <MessageCircle className="w-5 h-5" /><EditableText id="contact.cta" editMode={editMode} remoteContent={remoteContent} />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-12 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg border-2 border-primary flex items-center justify-center">
                <span className="text-primary font-black text-xs">SB</span>
              </div>
              <div>
                <p className="font-semibold text-sm">Sidi Bensoula</p>
                <p className="text-xs text-muted-foreground"><EditableText id="footer.tagline" editMode={editMode} remoteContent={remoteContent} /></p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/sidi_bensoula" target="_blank" rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"><SiInstagram className="w-5 h-5" /></a>
              <a href="https://www.facebook.com/sidibensoula" target="_blank" rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"><SiFacebook className="w-5 h-5" /></a>
            </div>
            <div className="text-center md:text-right space-y-1">
              <p className="text-xs text-muted-foreground"><EditableText id="footer.copyright" editMode={editMode} remoteContent={remoteContent} /></p>
              <Link to="/mentions-legales" className="text-xs text-primary hover:underline block">
                Mentions légales & Confidentialité
              </Link>
            </div>
          </div>
        </div>
      </footer>

      <ModuleModal open={modalOpen} onClose={() => setModalOpen(false)} mod={editingMod}
        onSave={handleSaveMod} onDelete={editingMod ? handleDeleteMod : undefined} />
    </div>
  )
}
